import { YStack } from "tamagui";

import { Grid, View, getGridLayout } from "@/components";
import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";

type MedicationItem = {
  id: string;
  drugClass: string;
  brandName: string;
  description: string;
  badge?: string;
  dosing: string;
};

// Matches the rendered height of the Badge component so cards stay aligned
const BADGE_PLACEHOLDER_HEIGHT = 28;

const MEDICATIONS: MedicationItem[] = [
  {
    id: "wegovy-pill",
    drugClass: "Semaglutide",
    brandName: "Wegovy® pill",
    description:
      "One of the most clinically studied GLP-1 medications available.",
    badge: "#1 prescribed GLP-1",
    dosing: "Once daily oral tablet",
  },
  {
    id: "wegovy-pen",
    drugClass: "Semaglutide",
    brandName: "Wegovy® pen",
    description:
      "One of the most clinically studied GLP-1s available, in injectable form.",
    badge: "Most popular",
    dosing: "Once weekly injection",
  },
  {
    id: "zepbound",
    drugClass: "Tirzepatide",
    brandName: "Zepbound®",
    description:
      "Targets two hormone pathways, GLP-1 and GIP, for weight management support.",
    dosing: "Once weekly injection",
  },
  {
    id: "saxenda",
    drugClass: "Liraglutide",
    brandName: "Saxenda®",
    description:
      "A daily injectable that helps regulate appetite and supports gradual, steady weight loss.",
    dosing: "Once daily injection",
  },
];

function Badge({ label }: { label: string }) {
  return (
    <View
      backgroundColor="$fillBadgeTertiary"
      borderRadius={999}
      paddingHorizontal="$200"
      paddingVertical="$75"
      alignSelf="flex-start"
    >
      <Text size="$xs">{label}</Text>
    </View>
  );
}

function MedicationCard({ item }: { item: MedicationItem }) {
  return (
    <YStack
      backgroundColor="$backgroundGround"
      borderRadius="$200"
      borderWidth="$strokeWidth"
      borderColor="$strokeSubdued"
      padding="$300"
      gap="$200"
      flex={1}
    >
      <YStack gap="$50">
        <Text size="$xs" color="$textTertiary">
          {item.drugClass}
        </Text>
        <Text size="$lg" semibold>
          {item.brandName}
        </Text>
      </YStack>

      <Text size="$sm" color="$textPrimary" flex={1}>
        {item.description}
      </Text>

      {item.badge ? (
        <Badge label={item.badge} />
      ) : (
        <View height={BADGE_PLACEHOLDER_HEIGHT} />
      )}

      <View height={1} backgroundColor="$strokeSubdued" marginVertical="$100" />

      <Text size="$sm" color="$textSecondary">
        {item.dosing}
      </Text>
    </YStack>
  );
}

function NotSureCard({
  onPress,
  isPending,
}: {
  onPress: () => void;
  isPending: boolean;
}) {
  return (
    <YStack
      backgroundColor="$backgroundGround"
      borderRadius="$200"
      borderWidth="$strokeWidth"
      borderColor="$strokeSubdued"
      padding="$400"
      gap="$200"
      justifyContent="center"
      flex={1}
    >
      <Text size="$lg" semibold>
        Not sure which is right for you?
      </Text>
      <Text size="$sm" color="$textSecondary">
        We will prescribe the best option based on your health history and
        goals. A licensed clinician will ensure the recommended plan is the
        right fit for you.
      </Text>
      <Button
        mode="primary"
        alignSelf="flex-start"
        onPress={onPress}
        disabled={isPending}
      >
        See if you are eligible →
      </Button>
    </YStack>
  );
}

export default function Glp1MedicationCards({
  onPress,
  isPending,
}: {
  onPress: () => void;
  isPending: boolean;
}) {
  return (
    <YStack
      maxWidth="$landingPageConstraint"
      width="100%"
      backgroundColor="$backgroundGround"
      paddingVertical="$800"
      alignSelf="center"
      paddingHorizontal="$400"
      gap="$600"
      alignItems="center"
    >
      <YStack alignItems="center" gap="$100">
        <Text size="$xxl" textAlign="center">
          GLP-1 medications we prescribe
        </Text>
        <Text size="$md" textAlign="center" color="$textSecondary">
          All medications prescribed when appropriate following clinician
          evaluation.
        </Text>
      </YStack>

      <Grid alignItems="stretch" $lg={{ maxWidth: "100%" }}>
        {MEDICATIONS.map((med) => (
          <YStack key={med.id} width="100%" $lg={getGridLayout(4).$lg}>
            <MedicationCard item={med} />
          </YStack>
        ))}
        <YStack width="100%" $lg={getGridLayout(8).$lg}>
          <NotSureCard onPress={onPress} isPending={isPending} />
        </YStack>
      </Grid>
    </YStack>
  );
}
